/**
 * ★★ WO lote 2b — A GRADE MENSAL. Puro: dias × indicadores, no formato da planilha do dono.
 *
 * ★★★ ESTE MÓDULO NÃO CONTA NADA. Ele AGRUPA.
 *
 * As contagens vêm de `painelDoDia` (`produtividade/dia.js`), que está no ar desde 3/out com
 * 35 casos e **não foi tocado**. ⚠ Recontar aqui criaria uma segunda definição de "ligação do
 * dia" — foi assim que a casa acabou com três definições de comparecimento (55, 55 e 62) e
 * duas de data de desfecho. ★ O que a grade acrescenta é **semana, mês, meta e cor**.
 *
 * ★ Decisões do desenho aprovado (`docs/wo-desenho-aprovado.html`), lidas do HTML:
 *   - colunas seg–sáb; "Sem N" depois de cada semana; "Mês" no fim;
 *   - realizado: futuro "–", zero CINZA, senão verde `>=` meta / vermelho `<`;
 *   - ★ totais coloridos contra a **meta acumulada até hoje**, e **cinza quando zero**;
 *   - a linha de META fica **vazia** nos dias futuros;
 *   - a linha de TOTAL do bloco **não tem cor** (é soma de gente com metas diferentes).
 */

import { diaDaData, painelDoDia } from '@/produtividade/dia';
import { indexarPainel } from '@/produtividade/dia';
import {
  EXPEDIENTE_PADRAO, ehDiaUtil, expedienteDaPessoa, metaAcumulada, metaDoDia,
} from '@/produtividade/expediente';
import { ehFeriado } from '@/produtividade/feriados';
import { temFuncao } from '@/equipe/funcoes';

/** 'AAAA-MM' + n → 'AAAA-MM-DD'. */
const diaDoMes = (mes, n) => `${mes}-${String(n).padStart(2, '0')}`;

/** 'AAAA-MM-DD' → o dia seguinte. */
const diaSeguinte = (dia) => {
  const [a, m, d] = dia.split('-').map(Number);
  const x = new Date(Date.UTC(a, m - 1, d + 1));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}-${String(x.getUTCDate()).padStart(2, '0')}`;
};

/** ★ O domingo que fecha uma semana de `semanaDe` (seg–sáb): é dela, como no Placar. */
export const domingoDaSemana = (semana) => diaSeguinte(semana[semana.length - 1]);

/** Quantos dias tem o mês. ★ Dia 0 do seguinte — sem tabela de 28/29/30/31. */
export const diasNoMes = (mes) => {
  const [a, m] = String(mes ?? '').split('-').map(Number);
  return a && m ? new Date(Date.UTC(a, m, 0)).getUTCDate() : 0;
};

/**
 * ★★ AS SEMANAS DO MÊS, de segunda a sábado. **Domingo NÃO entra** (decisão do dono: a
 * clínica não atende domingo).
 *
 * ⚠ A primeira e a última semana podem ser PARCIAIS — setembro/2026 começa numa terça, então
 * a semana 1 tem 5 colunas. O desenho já mostra isso.
 */
export function semanasDoMes(mes) {
  const total = diasNoMes(mes);
  const semanas = [];
  let atual = [];
  for (let n = 1; n <= total; n += 1) {
    const dia = diaDoMes(mes, n);
    const [a, m] = mes.split('-').map(Number);
    const dow = new Date(Date.UTC(a, m - 1, n)).getUTCDay();
    if (dow === 0) {                       // ★ domingo fecha a semana e não vira coluna
      if (atual.length) { semanas.push(atual); atual = []; }
      continue;
    }
    atual.push(dia);
  }
  if (atual.length) semanas.push(atual);
  return semanas;
}

/** Os domingos do mês — ★ o que a grade NÃO mostra, e por isso precisa declarar. */
export function domingosDoMes(mes) {
  const [a, m] = String(mes ?? '').split('-').map(Number);
  const fora = [];
  for (let n = 1; n <= diasNoMes(mes); n += 1) {
    if (new Date(Date.UTC(a, m - 1, n)).getUTCDay() === 0) fora.push(diaDoMes(mes, n));
  }
  return fora;
}

/**
 * O painel de CADA dia do mês, a partir dos dados do mês inteiro.
 *
 * ★ Agrupa as interações por dia (pelo fuso da clínica, com `diaDaData`) e chama o
 * `painelDoDia` que já existe. ⚠ Os agendamentos e os leads vão INTEIROS para cada dia: o
 * `painelDoDia` já recorta por dia por dentro, e pré-filtrar aqui duplicaria essa regra.
 *
 * ★★ Indicadores (01/11/2026): o recorte por dia dos leads e agendamentos é feito UMA vez
 * (`indexarPainel`) e entregue a cada dia. Antes, cada dia repassava os 8.884 leads.
 */
export function painelPorDia({
  dias = [], interacoes = [], leads = [], agendamentos = [], equipe = [],
  telefonesEquipe = [], agora = new Date(),
} = {}) {
  const porDia = new Map();
  for (const i of interacoes ?? []) {
    const d = diaDaData(i?.criadoEm?.toDate?.() ?? i?.criadoEm);
    if (!d) continue;
    if (!porDia.has(d)) porDia.set(d, []);
    porDia.get(d).push(i);
  }
  const indice = indexarPainel({ leads, agendamentos, equipe, telefonesEquipe });
  const saida = new Map();
  for (const dia of dias) {
    saida.set(dia, painelDoDia({
      dia, interacoes: porDia.get(dia) ?? [], leads, agendamentos, equipe, telefonesEquipe, agora, indice,
    }));
  }
  return saida;
}

/**
 * ★★ Quantos registros DA EQUIPE caíram nos dias que a grade não mostra.
 *
 * ⚠⚠ A PRIMEIRA VERSÃO NÃO OLHAVA AUTOR, e o número medido em produção derrubou a decisão:
 * setembro tinha **254** registros em domingo — mas **213 eram robô** (`migracao-vg` 157,
 * `sistema` 33, `importacao-planilha-2026` 23) e só **41** eram da equipe.
 *
 * ★ "254 registros caíram em domingo e não aparecem na grade" lê-se como **254 trabalhos
 * perdidos**. Uma migração que rodou num domingo não é trabalho de ninguém que sumiu.
 * ★★ É o mesmo susto que "757 agendamentos sem baixa" daria sem dizer que 698 são importação
 * — e, naquele caso, a medição mudou o plano antes de virar código. Aqui mudou depois, mas
 * mudou pelo mesmo motivo: **número certo e inútil é pior que número ausente**, porque
 * ninguém o contesta.
 *
 * ★ Agora conta só quem tem `autorId` na equipe — a mesma regra que `painelDoDia` usa para
 * decidir o que é trabalho de gente.
 */
export function registrosEmDomingo(mes, interacoes = [], equipe = []) {
  return registrosNosDias(domingosDoMes(mes), interacoes, equipe);
}

/** ★ A mesma conta de `registrosEmDomingo`, para uma lista de dias — a semana tem um domingo só. */
export function registrosNosDias(dias = [], interacoes = [], equipe = []) {
  const fora = new Set(dias);
  const daEquipe = new Set((equipe ?? []).map((u) => u?.id).filter(Boolean));
  let n = 0;
  for (const i of interacoes ?? []) {
    if (!i?.autorId || !daEquipe.has(i.autorId)) continue;
    const d = diaDaData(i?.criadoEm?.toDate?.() ?? i?.criadoEm);
    if (d && fora.has(d)) n += 1;
  }
  return n;
}

/**
 * ★★ A COR DE UMA CÉLULA DE REALIZADO. Quatro estados, e a ordem importa.
 *
 * ⚠ `zero` vem ANTES da comparação com a meta, de propósito: **nada registrado é CINZA, não
 * vermelho.** Vermelho é "trabalhou abaixo da meta"; cinza é "não há dado". Pintar de
 * vermelho um dia sem registro acusaria de baixo desempenho quem estava de folga.
 */
export function corDaCelula({ valor, meta, futuro }) {
  if (futuro) return 'futuro';
  if (!valor) return 'zero';
  if (!meta) return '';
  return valor >= meta ? 'ok' : 'bad';
}

/** ★ A cor de um TOTAL (semana/mês), contra a meta ACUMULADA ATÉ HOJE. Mesma ordem. */
export function corDoTotal({ valor, metaAcum }) {
  if (!metaAcum) return '';
  if (!valor) return 'zero';
  return valor >= metaAcum ? 'ok' : 'bad';
}

/** Os indicadores de cada espaço, na ordem do desenho. */
export const BLOCOS_SDR = [
  { id: 'ligacoes', titulo: 'Ligações (tentativas em leads distintos)', campo: 'leadsTentados', meta: 'ligacoesDia' },
  /**
   * ★★ "quem marcou" NÃO É DETALHE DE RÓTULO — é a resposta a uma divergência medida.
   *
   * ⚠ Esta tela conta pelo `agendadoPorId`: **quem apertou o botão**. O Placar e a tela de
   * Metas contam pelo `creditoSdr` do lead: **de quem é o lead**. Medido em setembro, a
   * mesma pessoa dá **20 aqui e 96 lá** — a Jéssica é a SDR original de 7.834 dos 7.964
   * leads, então "agendamentos dos leads dela" é quase "todos da clínica".
   *
   * ★ Decisão do dono (3/out): **agora**, cada tela diz o que conta; **no lote 3b**, Metas e
   * Placar passam a contar quem marcou, e aí os dois números se encontram.
   */
  { id: 'agendamentos', titulo: 'Agendamentos (quem marcou)', campo: 'agendou', meta: 'agendamentosDia', pendente: 'agendouSemBaixa' },  // ★ lote 3b: Metas e Placar passaram a usar a MESMA régua
  /**
   * ★★★ A-10.4 (16/out/2026) — O COMPARECIMENTO TAMBÉM PASSOU A SER "DE QUEM AGENDOU".
   *
   * ⚠⚠ Até aqui o rótulo era *"(do SDR do lead)"*, e era verdade: o comparecimento seguia
   * `creditoSdr`. ★ Decisão do dono: ele passa a seguir o `agendadoPorId` do agendamento
   * `compareceu` (`creditoComparecimento`), com reserva no `sdrId` quando o autor não é
   * pessoa da equipe.
   *
   * ⚠ Não é a MESMA régua do agendamento acima, e a diferença importa: ali conta-se **o
   * agendamento**, aqui conta-se **o paciente** (um por lead, pela regra única da WK). ★ O
   * autor é o mesmo campo; a unidade é outra.
   */
  { id: 'comparecimentos', titulo: 'Comparecimentos (de quem agendou)', campo: 'compareceu', meta: 'comparecimentosDia' },
];
export const BLOCOS_NEGOCIADOR = [
  { id: 'followups', titulo: 'Follow-ups (registros em leads que não fecharam)', campo: 'followUps', meta: 'followUpsDia' },
];

/**
 * ★★ A GRADE INTEIRA — do mês, ou de UMA semana.
 *
 * @param mes      'AAAA-MM'
 * @param semana   ★ Indicadores (01/11/2026): os 6 dias (seg–sáb) de `semanaDe`, no lugar do
 *                 mês. O período vai da segunda ao DOMINGO seguinte (o domingo é da semana
 *                 que acabou, como no Placar), então "hoje" num domingo ainda é desta semana.
 * @param metasPorMes ★ com `semana`: `{ 'AAAA-MM': metas }` — a semana pode atravessar dois
 *                 meses, e a meta de cada dia vem do documento do mês DELE
 * @param porDia   Map dia → saída de `painelDoDia` (use `painelPorDia`)
 * @param equipe   `[{ id, nome, funcoes, ativo }]`
 * @param metas    `{ [uid]: { ligacoesDia, agendamentosDia, comparecimentosDia, followUpsDia, conversao } }`
 *                 ⚠ **vazio no lote 2b** — as metas por dia nascem no lote 3. Sem meta, as
 *                 linhas de meta vêm vazias e nada é colorido. É honesto e não inventa alvo.
 * @param hoje     'AAAA-MM-DD' ou null quando o mês exibido não é o atual
 * @param config   `config.expediente`
 * @param feriados ★ 02/11/2026 — 'AAAA-MM-DD' sem meta (`feriadosDaClinica`). O que se fez no
 *                 feriado CONTA (D5); só a meta sai. Ausente ou `[]` = a grade de antes, idêntica.
 */
export function montarGrade({
  mes, semana = null, porDia = new Map(), equipe = [], metas = {}, metasPorMes = null, hoje = null,
  config = EXPEDIENTE_PADRAO, registrosForaDaGrade = 0, feriados = [],
} = {}) {
  const semanas = semana ? [semana] : semanasDoMes(mes);
  const dias = semanas.flat();
  const domingo = semana ? domingoDaSemana(semana) : null;
  const noPeriodo = semana
    ? Boolean(hoje) && hoje >= semana[0] && hoje <= domingo
    : Boolean(hoje) && hoje.startsWith(mes);
  const metasDoDia = (d) => (metasPorMes ? metasPorMes[d.slice(0, 7)] ?? {} : metas);
  /**
   * ★★ O LIMITE DA META ACUMULADA, e esta linha é a decisão A.3.1.
   *
   * Mês ATUAL → acumula até hoje. Mês PASSADO (`hoje` fora do mês) → o mês inteiro.
   * ⚠ Sem isto, abrir setembro mostraria tudo verde contra a meta de um punhado de dias:
   * elogio falso, que é pior que número errado — ninguém o contesta.
   */
  const limite = noPeriodo ? hoje : dias[dias.length - 1];
  const ateLimite = (ds) => ds.filter((d) => d <= limite);
  const ehFuturo = (d) => d > limite && noPeriodo;

  const valorDe = (dia, uid, campo) =>
    porDia.get(dia)?.porPessoa?.find((p) => p.id === uid)?.[campo] ?? 0;

  const linhaDaPessoa = (pessoa, bloco) => {
    const metaDiariaDe = (d) => Number(metasDoDia(d)?.[pessoa.id]?.[bloco.meta]) || 0;
    const metaDiaria = metaDiariaDe(limite);
    /**
     * ★★★ A-2 — A LINHA É DA PESSOA, A COLUNA É DA CLÍNICA. É a distinção da peça.
     *
     * ★ A **meta de cada célula** sai do expediente DELA: ⚠ quem trabalha sábado tem meta no
     * sábado, quem não trabalha tem zero.
     * ⚠⚠ Mas a **coluna** (o cinza de "dia não útil", em `Grade.jsx`) continua da CLÍNICA —
     * ★★★ se a Talita trabalha sábado e outra não, a coluna **não pode ser de ninguém**, senão a
     * grade muda de forma conforme quem está nela. Ver a medição, item (4) do E7.
     */
    const expedienteDela = expedienteDaPessoa(pessoa, config);
    const celulas = {};
    for (const d of dias) {
      const futuro = ehFuturo(d);
      const valor = futuro ? 0 : valorDe(d, pessoa.id, bloco.campo);
      const meta = metaDoDia(metaDiariaDe(d), d, expedienteDela, feriados);
      const feriado = ehFeriado(d, feriados);
      celulas[d] = {
        valor,
        // ★ feriado: a célula de meta fica VAZIA (não "0") e o realizado sem cor — nunca vermelho
        meta: futuro || feriado ? null : meta,
        pendente: bloco.pendente && !futuro ? valorDe(d, pessoa.id, bloco.pendente) : 0,
        futuro,
        cor: corDaCelula({ valor, meta, futuro }),
        // ⚠ a chave só existe no feriado: sem feriado, a célula é a de antes, byte a byte
        ...(feriado ? { feriado: true } : {}),
      };
    }
    const totalDe = (ds) => {
      const consideradosNaMeta = ateLimite(ds);
      const valor = ds.reduce((t, d) => t + (celulas[d].futuro ? 0 : celulas[d].valor), 0);
      const metaAcum = metasPorMes
        ? consideradosNaMeta.reduce((t, d) => t + metaAcumulada(metaDiariaDe(d), [d], expedienteDela, feriados), 0)
        : metaAcumulada(metaDiaria, consideradosNaMeta, expedienteDela, feriados);
      return { valor, metaAcum, cor: corDoTotal({ valor, metaAcum }) };
    };
    return {
      id: pessoa.id,
      nome: pessoa.nome ?? '',
      metaDiaria,
      celulas,
      semanas: semanas.map(totalDe),
      mes: totalDe(dias),
      ...(metasPorMes ? { metaDiariaPorDia: Object.fromEntries(dias.map((d) => [d, metaDiariaDe(d)])) } : {}),
    };
  };

  const montarBloco = (bloco, pessoas) => {
    const linhas = pessoas.map((p) => linhaDaPessoa(p, bloco));
    /**
     * ★ O TOTAL do bloco NÃO É COLORIDO, e é decisão do desenho.
     * ⚠ Somar metas de pessoas diferentes e comparar com a soma dos realizados esconde quem
     * ficou atrás: duas pessoas, uma a 200% e outra a 20%, dariam verde.
     */
    const totalCelulas = {};
    for (const d of dias) {
      totalCelulas[d] = {
        valor: linhas.reduce((t, l) => t + l.celulas[d].valor, 0),
        futuro: ehFuturo(d),
      };
    }
    return {
      ...bloco,
      linhas,
      // ★ Só existe quando há mais de uma pessoa: um "total" de uma linha só é a própria linha
      // repetida, e o desenho não o mostra.
      total: linhas.length > 1 ? {
        celulas: totalCelulas,
        semanas: semanas.map((_, i) => linhas.reduce((t, l) => t + l.semanas[i].valor, 0)),
        mes: linhas.reduce((t, l) => t + l.mes.valor, 0),
      } : null,
    };
  };

  const ativos = (equipe ?? []).filter((u) => u?.ativo !== false);
  const sdrs = ativos.filter((u) => temFuncao(u, 'sdr'));
  const negociadores = ativos.filter((u) => temFuncao(u, 'negociador'));

  return {
    mes,
    hoje: noPeriodo ? hoje : null,
    semanas,
    dias,
    limite,
    diasUteis: dias.filter((d) => ehDiaUtil(d, config)),
    // ★ os feriados do período, para a tela marcar a coluna; só existe quando há algum
    ...(dias.some((d) => ehFeriado(d, feriados)) ? { feriados: dias.filter((d) => ehFeriado(d, feriados)) } : {}),
    foraDaGrade: { domingos: semana ? [domingo] : domingosDoMes(mes), registros: registrosForaDaGrade },
    semMetas: metasPorMes
      ? Object.values(metasPorMes).every((m) => Object.keys(m ?? {}).length === 0)
      : Object.keys(metas ?? {}).length === 0,
    blocos: [
      ...BLOCOS_SDR.map((b) => ({ espaco: 'sdr', ...montarBloco(b, sdrs) })),
      ...BLOCOS_NEGOCIADOR.map((b) => ({ espaco: 'negociador', ...montarBloco(b, negociadores) })),
    ],
    ...(semana ? { visao: 'semana' } : {}),
  };
}
